import type { CityConfig } from "../city-template/types";

export const mercedConfig: CityConfig = {
  city: "Merced",
  state: "California",
  stateAbbr: "CA",
  slug: "merced",
  route: "/cities/merced",
  title: "Emergency Passport Merced, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Merced, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Merced County and Central Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Merced, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Merced and Central Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Merced%20California%20cityscape%20at%20golden%20hour%20with%20UC%20Merced%20campus%20buildings%20visible%2C%20modern%20architecture%20surrounded%20by%20rolling%20agricultural%20fields%20and%20orchards%2C%20Sierra%20Nevada%20foothills%20in%20the%20distant%20background%2C%20warm%20sunset%20light%20over%20the%20Central%20Valley%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20serene%20rural%20California%20scenery%20with%20green%20farmland&width=1800&height=700&seq=merced-hero-001&orientation=landscape",
  heroImageAlt: "Merced California cityscape with UC Merced campus, agricultural fields, and Sierra Nevada foothills at golden hour",
  geoPosition: "37.3022;-120.4830",
  geoPlacename: "Merced, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown SF — approximately 2 hours west of Merced via CA-152 and I-680",
  },
  nearbyCitiesZips: [
    { name: "Merced", zips: "95340–95344, 95348" },
    { name: "Atwater", zips: "95301" },
    { name: "Livingston", zips: "95334" },
    { name: "Turlock", zips: "95380, 95382" },
    { name: "Los Banos", zips: "93635" },
    { name: "Chowchilla", zips: "93610" },
    { name: "Madera", zips: "93637, 93638" },
    { name: "Fresno", zips: "93650, 93701–93730" },
    { name: "Modesto", zips: "95350–95358" },
    { name: "Ceres", zips: "95307" },
    { name: "Gustine", zips: "95322" },
    { name: "Dos Palos", zips: "93620" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Central Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fresno", route: "/cities/fresno" },
    { name: "Modesto", route: "/cities/modesto" },
    { name: "Stockton", route: "/cities/stockton" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "Visalia", route: "/cities/visalia" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Merced remotely via secure courier. We do not have a walk-in office in Merced — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Central Valley travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Merced County",
  serviceAreaIntro: "From the UC Merced campus to the historic downtown, from the agricultural heartland to the Sierra Nevada foothills — Fast Passport Center serves the entire Merced community. We regularly help travelers from Atwater, Livingston, Turlock, and Los Banos get their passports fast.",
  nearbyCitiesIntro: "We serve all Merced neighborhoods and nearby Central Valley communities including Atwater, Livingston, Turlock, Chowchilla, and Los Banos. If you are in Merced, Madera, or Stanislaus County, we have you covered.",
  howItWorksIntro: "Most Merced travelers complete everything online and ship documents overnight. No need to drive to Fresno or the Bay Area or wait in line at the regional agency.",
  mistakesIntro: "With Merced being a gateway to Yosemite and a growing hub for UC Merced international students, local travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Merced and Central Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Merced? Here are the most common ones we hear from Central Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Central Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer H.",
      location: "Merced, CA",
      rating: 5,
      text: "My study abroad trip to Spain was in 4 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved my semester.",
      service: "Emergency Passport",
    },
    {
      name: "Michael T.",
      location: "Atwater, CA",
      rating: 5,
      text: "Needed to renew for a family reunion in the Philippines. The process was smooth, they reviewed my documents, and I had my passport in 4 days.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah K.",
      location: "Turlock, CA",
      rating: 5,
      text: "Lost my passport the week before my vacation to Costa Rica. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible.",
      service: "Lost Passport",
    },
    {
      name: "David R.",
      location: "Livingston, CA",
      rating: 5,
      text: "Living in a small Central Valley town, I thought I'd have to drive to Fresno. Fast Passport Center handled everything remotely. My passport arrived in 3 days.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "merced-1",
      name: "Merced Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1750 W 16th St",
      city: "Merced",
      state: "CA",
      zip: "95340",
      phone: "(209) 383-1351",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Merced",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "merced-2",
      name: "Atwater Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1390 Broadway Ave",
      city: "Atwater",
      state: "CA",
      zip: "95301",
      phone: "(209) 358-5111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles north of Merced",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "merced-3",
      name: "Turlock Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "555 E Main St",
      city: "Turlock",
      state: "CA",
      zip: "95380",
      phone: "(209) 632-0111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles north of Merced",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Stanislaus County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "merced-4",
      name: "Los Banos Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1200 Pacheco Blvd",
      city: "Los Banos",
      state: "CA",
      zip: "93635",
      phone: "(209) 826-0111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles west of Merced",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for western Merced County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "merced-5",
      name: "Merced County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2222 M St",
      city: "Merced",
      state: "CA",
      zip: "95340",
      phone: "(209) 385-7517",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Merced County Courthouse",
      website: "https://www.co.merced.ca.us/",
      websiteLabel: "Merced County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "merced-6",
      name: "Fresno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2309 Tulare St",
      city: "Fresno",
      state: "CA",
      zip: "93721",
      phone: "(559) 498-0111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "60 miles south of Merced",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Merced County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 2+ hours to San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Merced photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Central Valley acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Merced applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Merced families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Merced travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Merced." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Merced address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Merced before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Merced for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Central Valley travelers." },
    { q: "How fast can I renew my passport in Merced?", a: "Fast Passport Center offers fast passport renewal in Merced in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Merced in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Merced?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Merced residents." },
    { q: "Can I expedite a child passport in Merced?", a: "Yes. Fast Passport Center expedites child passports in Merced for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Merced with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Merced?",
      a: "With Fast Passport Center's emergency courier service, Merced travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to San Francisco or Fresno for a passport?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Merced?",
      a: "Popular options include the Merced Main Post Office (95340), Merced County Clerk (95340), Atwater Post Office (95301), and Turlock Post Office (95380). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Merced?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in a rural area near Merced?",
      a: "We serve all of Merced County and the surrounding Central Valley including Atwater, Livingston, Chowchilla, Los Banos, and Dos Palos. Our overnight shipping labels work from any UPS location, so your rural location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Merced?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Merced?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Merced and Atwater offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Merced?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Merced?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Merced families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Merced", address: "1730 W 16th St, Merced, CA 95340", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Merced", address: "1460 W Main St, Merced, CA 95340", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Merced", address: "1340 W Main St, Merced, CA 95340", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Merced", address: "3360 G St, Merced, CA 95340", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Merced", address: "3050 Loughborough Dr, Merced, CA 95340", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Merced", address: "1230 W Main St, Merced, CA 95340", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Turlock", address: "2160 Geer Rd, Turlock, CA 95382", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Atwater", address: "800 Commerce Ave, Atwater, CA 95301", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Merced",
      address: "1313 W 16th St, Merced, CA 95340",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Turlock",
      address: "1310 W Main St, Turlock, CA 95380",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Atwater",
      address: "1200 Winton Way, Atwater, CA 95301",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Los Banos",
      address: "1110 Pacheco Blvd, Los Banos, CA 93635",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for western Merced County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "MCE",
      name: "Merced Regional Airport",
      distance: "3 miles from downtown Merced",
      address: "2090 W Childs Ave, Merced, CA 95341",
      note: "Regional airport with commercial flights to major hubs. Growing service for Central Valley travelers.",
      flightUrl: "https://www.mercedairport.com/",
      flightLabel: "Merced Regional Airport",
      featured: true,
    },
    {
      code: "FAT",
      name: "Fresno Yosemite International Airport",
      distance: "60 miles south of Merced",
      address: "5175 E Clinton Way, Fresno, CA 93727",
      note: "The primary international airport for the Central Valley. Flights to Mexico and international connections.",
      flightUrl: "https://www.fresno.gov/departments/airport/",
      flightLabel: "Fresno Airport",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "130 miles west of Merced",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and beyond. Accessible via CA-152.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Merced County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Merced County",
      url: "https://www.co.merced.ca.us/143/Clerk-Recorder",
    },
    {
      label: "Merced County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Merced County",
      url: "https://www.merced.courts.ca.gov/",
    },
  ],
};